import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { FreemiumModal } from './FreemiumModalBase';
import { parseISO, isAfter, add } from 'date-fns';

import { FreemiumModalContent } from './FreemiumModalContent';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { TrackOnMount } from 'src/libs/Analytics';

const hardToFillCountryToDatePostedMap: Record<string, number> = {
  PH: 7,
  ID: 7,
  MY: 14,
  SG: 14,
  TH: 7,
  HK: 7,
};
const shouldShowHardToFill = ({
  jobCreatedDate,
  numberOfApplications,
  postingLocationCountry,
}: {
  jobCreatedDate: string;
  numberOfApplications: number;
  postingLocationCountry: string;
}): boolean => {
  const jobCreatedIsoDate = parseISO(jobCreatedDate);

  if (!hardToFillCountryToDatePostedMap[postingLocationCountry]) {
    return false;
  }

  return Boolean(
    isAfter(
      Date.now(),
      add(jobCreatedIsoDate, {
        days: hardToFillCountryToDatePostedMap[postingLocationCountry],
      }),
    ) && numberOfApplications < 20,
  );
};

interface FreemiumHardToFillModalProps {
  link: string;
  jobCreatedDate: string;
  numberOfApplications: number;
  postingLocationCountry: string;
  trackButtonPressed: () => void;
  trackModalDisplayed: () => void;
  trackModalDismissed: () => void;
}

export const FreemiumHardToFillModal = ({
  link,
  jobCreatedDate,
  numberOfApplications,
  postingLocationCountry,
  trackModalDisplayed,
  trackButtonPressed,
  trackModalDismissed,
}: FreemiumHardToFillModalProps) => {
  const { t } = useTranslations(translations);

  const { getFreemiumHardToFillModalStatus, hideFreemiumHardToFillModal } =
    useUserActionStatus('freemium-hard-to-fill');

  const showModal =
    shouldShowHardToFill({
      jobCreatedDate,
      numberOfApplications,
      postingLocationCountry,
    }) && getFreemiumHardToFillModalStatus() === 'visible';

  if (!showModal) {
    return null;
  }

  const titleId = 'FreemiumHardToFillModal';

  const closeModal = () => {
    trackModalDismissed();
    hideFreemiumHardToFillModal();
  };

  return (
    <TrackOnMount onMount={trackModalDisplayed}>
      <FreemiumModal
        isOpen={showModal}
        titleId={titleId}
        close={closeModal}
        content={
          <FreemiumModalContent
            titleId={titleId}
            subheading={t(
              'Upgrade anytime to help you find candidates faster.',
            )}
            heading={t('Struggling to fill this role?')}
            upgradeTrackingFn={trackButtonPressed}
            dotpoints={[
              {
                heading: t('Greater visibility:'),
                description: t(
                  'Put your ad at the top of search results, reaching more candidates.',
                ),
              },
              {
                heading: t('Extend your ad for 30 days:'),
                description: t(
                  'Keeps your ad active for an additional 30 days, giving you more time to attract the right candidates.',
                ),
              },
              {
                heading: t('Access to Recommended Candidates:'),
                description: t(
                  'Get a curated list of candidates whose skills and experience match your job ad.',
                ),
              },
            ]}
            link={link}
          />
        }
      />
    </TrackOnMount>
  );
};
